// TipTap 扩展：段落/标题/列表以及字体相关能力的增强
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { Extension } from '@tiptap/core';

// 字体家族扩展：以 textStyle 标记承载 font-family 属性
// 说明：
// - parseHTML：从节点的内联 style 中提取 font-family
// - renderHTML：在渲染时将 font-family 写回到 style
// - 命令：通过 setMark('textStyle', { fontFamily }) 将字体应用到选区/全文
export const FontFamily = Extension.create({
  name: 'fontFamily',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: element => element.style?.fontFamily || null,
            renderHTML: attrs => (attrs.fontFamily ? { style: `font-family:${attrs.fontFamily}` } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontFamily:
        font => ({ chain }) => chain().setMark('textStyle', { fontFamily: font }).run(),
    };
  },
});

// 字号扩展：以 textStyle 标记承载 font-size 属性
// 说明：
// - 与 FontFamily 相同方式承载，在编辑器内部统一用 textStyle mark 管理
export const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style?.fontSize || null,
            renderHTML: attrs => (attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        size => ({ chain }) => chain().setMark('textStyle', { fontSize: size }).run(),
    };
  },
});

// 段落扩展：支持缩进/行高/外边距/对齐以及上下边框线
// 渲染策略：
// - 将已声明的属性聚合为 style 字符串写入，保持原有 style 不丢失
export const StyledParagraph = Paragraph.extend({
  addAttributes() {
    return {
      textIndent: { default: null, parseHTML: el => el.style?.textIndent || null },
      lineHeight: { default: null, parseHTML: el => el.style?.lineHeight || null },
      marginTop: { default: null, parseHTML: el => el.style?.marginTop || null },
      marginRight: { default: null, parseHTML: el => el.style?.marginRight || null },
      marginBottom: { default: null, parseHTML: el => el.style?.marginBottom || null },
      marginLeft: { default: null, parseHTML: el => el.style?.marginLeft || null },
      letterSpacing: { default: null, parseHTML: el => el.style?.letterSpacing || null },
      textAlign: { default: null, parseHTML: el => el.style?.textAlign || null },
      borderBottom: { default: null, parseHTML: el => el.style?.borderBottom || null },
      borderTop: { default: null, parseHTML: el => el.style?.borderTop || null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const styles = [];
    const s = (k) => { if (HTMLAttributes[k]) styles.push(`${k.replace(/[A-Z]/g,m=>'-'+m.toLowerCase())}:${HTMLAttributes[k]}`); };
    s('textIndent'); s('lineHeight'); s('marginTop'); s('marginRight'); s('marginBottom'); s('marginLeft'); s('letterSpacing'); s('textAlign'); s('borderBottom'); s('borderTop');
    const attrs = { ...HTMLAttributes };
    ['textIndent', 'lineHeight', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'letterSpacing', 'textAlign', 'borderBottom', 'borderTop']
      .forEach((k) => { delete attrs[k]; });
    if (styles.length) attrs.style = [HTMLAttributes.style || '', styles.join(';')].filter(Boolean).join(';');
    return ['p', attrs, 0];
  },
});

// 标题扩展：支持行高/外边距/对齐
// 渲染策略：
// - 根据节点 level 渲染 h1~h6；聚合 style 保持原样式
export const StyledHeading = Heading.extend({
  addAttributes() {
    return {
      lineHeight: { default: null, parseHTML: el => el.style?.lineHeight || null },
      marginTop: { default: null, parseHTML: el => el.style?.marginTop || null },
      marginRight: { default: null, parseHTML: el => el.style?.marginRight || null },
      marginBottom: { default: null, parseHTML: el => el.style?.marginBottom || null },
      marginLeft: { default: null, parseHTML: el => el.style?.marginLeft || null },
      textAlign: { default: null, parseHTML: el => el.style?.textAlign || null },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    const level = node.attrs.level;
    const styles = [];
    const s = (k) => { if (HTMLAttributes[k]) styles.push(`${k.replace(/[A-Z]/g,m=>'-'+m.toLowerCase())}:${HTMLAttributes[k]}`); };
    s('lineHeight'); s('marginTop'); s('marginRight'); s('marginBottom'); s('marginLeft'); s('textAlign');
    const attrs = { ...HTMLAttributes };
    ['lineHeight', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'textAlign']
      .forEach((k) => { delete attrs[k]; });
    if (styles.length) attrs.style = [HTMLAttributes.style || '', styles.join(';')].filter(Boolean).join(';');
    return [`h${level}`, attrs, 0];
  },
});

// 列表扩展：支持左缩进/内边距/列表符类型
// 说明：
// - Word/Aspose 导出常见列表通过 normalizeAsposeLists 转为语义化 <ul>/<ol>
// - 此处用于保留/调整缩进与符号类型，保证打印/导出一致性
export const StyledBulletList = BulletList.extend({
  addAttributes() {
    return {
      marginLeft: { default: null, parseHTML: el => el.style?.marginLeft || null },
      paddingLeft: { default: null, parseHTML: el => el.style?.paddingLeft || null },
      listStyleType: { default: null, parseHTML: el => el.style?.listStyleType || null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const styles = [];
    const s = (k) => { if (HTMLAttributes[k]) styles.push(`${k.replace(/[A-Z]/g,m=>'-'+m.toLowerCase())}:${HTMLAttributes[k]}`); };
    s('marginLeft'); s('paddingLeft'); s('listStyleType');
    const attrs = { ...HTMLAttributes };
    ['marginLeft', 'paddingLeft', 'listStyleType'].forEach((k) => { delete attrs[k]; });
    if (styles.length) attrs.style = [HTMLAttributes.style || '', styles.join(';')].filter(Boolean).join(';');
    return ['ul', attrs, 0];
  },
});

export const StyledOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      marginLeft: { default: null, parseHTML: el => el.style?.marginLeft || null },
      paddingLeft: { default: null, parseHTML: el => el.style?.paddingLeft || null },
      listStyleType: { default: null, parseHTML: el => el.style?.listStyleType || null },
    };
  },
  renderHTML({ HTMLAttributes }) {
    const styles = [];
    const s = (k) => { if (HTMLAttributes[k]) styles.push(`${k.replace(/[A-Z]/g,m=>'-'+m.toLowerCase())}:${HTMLAttributes[k]}`); };
    s('marginLeft'); s('paddingLeft'); s('listStyleType');
    const attrs = { ...HTMLAttributes };
    ['marginLeft', 'paddingLeft', 'listStyleType'].forEach((k) => { delete attrs[k]; });
    if (styles.length) attrs.style = [HTMLAttributes.style || '', styles.join(';')].filter(Boolean).join(';');
    return ['ol', attrs, 0];
  },
});
